import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { isIdentityMatch } from '@app/core/misc/identity-matching';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { BlaCompany } from 'src/app/core/entity/bla-company';
import { Client } from 'src/app/core/entity/client';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { CloudCompanyService } from 'src/app/core/services/cloud-company.service';
import { ToastActions } from 'src/app/core/state/toast/toast.actions';
import { TableColumn } from 'src/app/shared/components/table/interfaces/table-column';
import { translateColumnHeaders } from 'src/app/shared/components/table/misc/translate-column-header';
import { BaseCloudImportExportComponent } from './base-cloud-import-export.component';

type ImportTableRowType = BlaCompany & { statusText: string };

@Component({
  selector: 'app-import-missing-companies-to-byst',
  templateUrl: './import-missing-companies-to-byst.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: BaseCloudImportExportComponent, useExisting: ImportMissingCompaniesToBystComponent },
    getTranslationProvider('ImportMissingCompaniesToBystComponent'),
  ],
})
export class ImportMissingCompaniesToBystComponent extends BaseCloudImportExportComponent {
  @Input()
  set includeHidden(value: boolean) {
    this.filterSubject.next({ includeHidden: value });
  }

  @Output() updating: EventEmitter<void> = new EventEmitter<void>();
  @Output() updated: EventEmitter<void> = new EventEmitter<void>();

  cloudCompanies$: Observable<ImportTableRowType[]>;
  selectedCloudCompanies: BlaCompany[] = [];
  columns$: Observable<TableColumn[]>;

  get hasSelectedCompanies() {
    return this.selectedCloudCompanies.length > 0;
  }

  private columns = [
    { field: 'orgNumber', header: 'orgNumber' },
    { field: 'name', header: 'name' },
    { field: 'systemNote', header: 'systemNote' },
    { field: 'publicKey', header: 'cloudDatabaseKey' },
    { field: 'statusText', header: 'infoAboutDatabase' },
  ];

  constructor(protected override cloudCompanyService: CloudCompanyService) {
    super(cloudCompanyService);

    this.cloudCompanies$ = combineLatest([this.loadedDataSubject, this.filterSubject]).pipe(
      map(([data, filter]) => data.filter((company: BlaCompany) => !company.hidden || filter.includeHidden)),
      map((data) => this.appendStatusText(data)),
    );

    this.columns$ = this.onTranslationChangeMapTo(translateColumnHeaders(this.columns as TableColumn[]));
  }

  loadList(): void {
    this.getListData().subscribe({
      next: (response: BlaCompany[]) => {
        this.loadedDataSubject.next(response);
      },
      error: (err: unknown) => console.log('Error loading import list', err),
    });
  }

  execute(): void {
    if (!this.hasSelectedCompanies) {
      return;
    }

    this.updating.emit();

    this.cloudCompanyService.importCloudCompaniesToByst(this.selectedCloudCompanies).subscribe(
      (response) => {
        if (!response.success && response.error) {
          this.store.dispatch(
            ToastActions.showErrorMessage({ summary: this.translate('anErrorOccured'), detail: response.error }),
          );
        }
        this.selectedCloudCompanies = [];
        this.appendClients(response.createdClients);
        this.loadList();
        this.updated.emit();
      },
      () => {
        this.updated.emit();
      },
    );
  }

  // Implementation of abstract parent method
  protected filterList(clients: Client[], blaCompanies: BlaCompany[]): BlaCompany[] {
    return this.filterCompanies(blaCompanies).filter((company) => {
      return !clients.some(
        (client) =>
          client.cloudApiKey === company.publicKey || isIdentityMatch(client.corporateIdentity, company.orgNumber),
      );
    });
  }

  private filterCompanies(blaCompanies: BlaCompany[]): BlaCompany[] {
    // exclude cloud companies without corp identity and public key
    return blaCompanies.filter((company: BlaCompany) => Boolean(company.orgNumber) && Boolean(company.publicKey));
  }

  private loadedDataSubject = new BehaviorSubject([]);
  private filterSubject = new BehaviorSubject({ includeHidden: false });

  private appendStatusText(cloudCompanies: BlaCompany[]): ImportTableRowType[] {
    const duplicatedOrgNumbers = this.getDuplicateOrgNumbers(cloudCompanies);

    return cloudCompanies.map((company) => {
      const statusText = [];

      if (company.hidden) {
        statusText.push(this.translate('hidden'));
      }

      if (duplicatedOrgNumbers.includes(company.orgNumber?.replace(/-/g, ''))) {
        statusText.push(this.translate('duplicate'));
      }

      return { ...company, statusText: statusText.join(', ') };
    });
  }

  private getDuplicateOrgNumbers(cloudCompanies: BlaCompany[]): string[] {
    return cloudCompanies
      .filter((company) => Boolean(company.orgNumber))
      .filter((company, index, self) => self.findIndex((t) => t.orgNumber === company.orgNumber) !== index)
      .map((company) => company.orgNumber.replace(/-/g, ''));
  }
}
